import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import jwtDecode from 'jwt-decode'
import * as api from '@/api/login.api'
import { actions as loginactions } from '@/actions/login.actions'
import { get, set, del } from 'idb-keyval'
import { actions as loaderActions } from '@/actions/loader.actions'

const AuthContext = createContext()

export const AuthProvider = ({ children, store }) => {
  const { dispatch } = store
  const [tokenChanged, setTokenChanged] = useState(null)
  const refreshTimeoutRef = useRef(null)

  const storeToken = async (token) => {
    const decoded = jwtDecode(token)
    await set('accessToken', token)
    await set('exp', decoded.exp)
    setTokenChanged(token)
  }

  const clearToken = async () => {
    await del('accessToken')
    await del('exp')
  }

  const login = async (username, password, token) => {
    // Aqui await SI tiene efecto aunque el IDE diga lo contrario. Sin él, no esperará la respuesta.
    const response = await dispatch(loaderActions.withGlobalLoader(() => api.login(username, password, token)))
    await storeToken(response.data.accessToken)
    dispatch(loginactions.login(response.data))
  }

  const logout = async () => {
    setTokenChanged(null)
    dispatch(loginactions.logout())
    await clearToken()
    dispatch(loaderActions.withGlobalLoader(() => api.logout()))
  }

  const refresh = async () => {
    try {
      // Aqui await SI tiene efecto aunque el IDE diga lo contrario. Sin él, no esperará la respuesta.
      const res = await dispatch(loaderActions.withGlobalLoader(() => api.refresh()))
      const data = res.data
      await storeToken(data)
      return data
    } catch (err) {
      await logout()
      return null
    }
  }

  const changeRol = async (role) => {
    // Aqui await SI tiene efecto aunque el IDE diga lo contrario. Sin él, no esperará la respuesta.
    const response = await dispatch(loaderActions.withGlobalLoader(() => api.changeRole(role)))
    await storeToken(response.data.accessToken)
    dispatch(loginactions.change(response))
  }

  const getValidAccessToken = async () => {
    const exp = await get('exp')
    const accessToken = await get('accessToken')
    if (exp && exp < Date.now() / 1000) {
      try {
        return await refresh()
      } catch (err) {
        return null
      }
    }

    return accessToken
  }

  useEffect(() => {
    const setupAutoRefresh = async () => {
      const exp = await get('exp')
      const accessToken = await get('accessToken')
      if (!accessToken || !exp || !tokenChanged) return
      const now = Date.now()
      const expireAt = exp * 1000
      const refreshTime = expireAt - now - 30000

      if (refreshTime <= 0) return

      refreshTimeoutRef.current = setTimeout(() => {
        if (document.visibilityState === 'visible') {
          refresh()
        }
      }, refreshTime)
    }

    setupAutoRefresh()

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const exp = await get('exp')
        const accessToken = await get('accessToken')
        const now = Date.now()
        if (!accessToken || !exp) return
        const expireAt = exp * 1000
        if (expireAt - now <= 60000) {
          await refresh()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearTimeout(refreshTimeoutRef.current)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [tokenChanged])

  return (
    <AuthContext.Provider value={{ login, logout, changeRol, getValidAccessToken }}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
